import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Table,
  Box,
  Button,
  Badge,
  Multiselect,
  Input,
  Pagination,
  Alert,
  ColumnLayout,
  FormField,
  Modal,
  Textarea,
  Icon,
} from '@cloudscape-design/components';
import { getDataService } from '../services/dataServiceFactory';
import { isUsingDirectDynamoDB } from '../services/dataServiceFactory';
import { filterVisibleMappings } from '../services/mappingVisibility';
import { getUserAlias } from '../auth/midway';
import { loadCommonControlsHierarchy } from '../services/commonControlsMetadataService';

const normalizeFrameworkCatalog = (catalog = '') =>
  (catalog || '').replace(/[-_]/g, '').toLowerCase();

const normalizeFrameworkKey = (frameworkKey = '') => {
  const [catalog = '', version = ''] = frameworkKey.split('#');
  return `${normalizeFrameworkCatalog(catalog)}#${version}`;
};

const isCommonControlsCatalogName = (catalog = '') =>
  normalizeFrameworkCatalog(catalog) === 'commoncontrols';

/**
 * ViewMappings Component - NEX-1061 & NEX-1062
 * Allows users to view/select/filter specific AWS services mappings
 * for in-scope industry frameworks as published by AWS
 *
 * NEX-1062: Added cascading filters for Control Domain/Objective/Common Control
 */
const ViewMappings = () => {
  const PREVIEW_MAPPING_LIMIT = 50;
  const FILTER_LOAD_DEBOUNCE_MS = 250;
  const usesDirectDynamoDB = isUsingDirectDynamoDB();

  // Data state
  const [mappingsData, setMappingsData] = useState([]);
  const [controlDetails, setControlDetails] = useState(new Map());
  const controlDetailsRef = useRef(controlDetails);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPreviewMode, setIsPreviewMode] = useState(true);
  const [canLoadFullMappings, setCanLoadFullMappings] = useState(false);

  // Filter metadata loaded independently of mappings
  const [frameworkMetadata, setFrameworkMetadata] = useState([]);
  const [awsServiceMetadata, setAwsServiceMetadata] = useState([]);
  const [filterMetadataLoading, setFilterMetadataLoading] = useState(true);

  // Common Controls hierarchy data (NEX-1062)
  const [commonControlsData, setCommonControlsData] = useState({
    domains: [],      // L1 - Control Domains
    objectives: [],   // L2 - Control Objectives
    controls: [],     // L3 - Common Controls
  });
  const [commonControlsLoading, setCommonControlsLoading] = useState(true);

  // Filter state
  const [selectedAwsServices, setSelectedAwsServices] = useState([]);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Common Controls filter state (NEX-1062) - Cascading filters
  const [selectedDomains, setSelectedDomains] = useState([]);      // L1 selections
  const [selectedObjectives, setSelectedObjectives] = useState([]); // L2 selections
  const [selectedCommonControls, setSelectedCommonControls] = useState([]); // L3 selections

  // Pagination state
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [itemsPerPage] = useState(25);

  // Voting/Feedback state (NEX-1064)
  const [userFeedback, setUserFeedback] = useState({}); // { mappingKey: { label, feedback, feedbackId } }
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [currentFeedbackMapping, setCurrentFeedbackMapping] = useState(null);
  const [feedbackReason, setFeedbackReason] = useState('');
  const [feedbackType, setFeedbackType] = useState(null); // 'thumbs_up' or 'thumbs_down'
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const lastScopedControlSignatureRef = useRef('');
  const activeLoadRequestRef = useRef(0);
  const hasLoadedDirectModeRef = useRef(false);
  const filterLoadDebounceRef = useRef(null);
  const feedbackLoadingIdsRef = useRef(new Set());

  useEffect(() => {
    controlDetailsRef.current = controlDetails;
  }, [controlDetails]);

  // Load mappings, user ID, and Common Controls on mount
  useEffect(() => {
    loadCurrentUser();
    loadFilterMetadata();
    loadCommonControls();
  }, []);

  // Load Common Controls hierarchy data (NEX-1062)
  const loadCommonControls = async () => {
    setCommonControlsLoading(true);
    try {
      const dataService = getDataService();
      const data = await loadCommonControlsHierarchy(dataService);
      setCommonControlsData(data);
    } catch (err) {
      console.error('Error loading Common Controls hierarchy:', err);
      // Non-critical error - don't block the UI
    } finally {
      setCommonControlsLoading(false);
    }
  };

  const loadFilterMetadata = async () => {
    setFilterMetadataLoading(true);
    try {
      const dataService = getDataService();
      const frameworks = await dataService.getAvailableFrameworks();

      const industryFrameworks = new Map();
      const awsServices = new Set();

      frameworks.forEach((framework) => {
        const frameworkName = framework?.frameworkName;
        const frameworkVersion = framework?.version;
        if (!frameworkName || !frameworkVersion) {
          return;
        }

        if (/^AWS[.-]?/i.test(frameworkName)) {
          const serviceName = frameworkName.replace(/^AWS[.-]?/i, '');
          if (serviceName) {
            awsServices.add(serviceName);
          }
          return;
        }

        if (isCommonControlsCatalogName(frameworkName)) {
          return;
        }

        const key = `${frameworkName}#${frameworkVersion}`;
        industryFrameworks.set(key, {
          label: `${frameworkName} ${frameworkVersion}`,
          value: key,
          catalog: frameworkName,
          version: frameworkVersion,
        });
      });

      setFrameworkMetadata(
        Array.from(industryFrameworks.values()).sort((a, b) =>
          a.label.localeCompare(b.label)
        )
      );
      setAwsServiceMetadata(
        Array.from(awsServices)
          .sort()
          .map((service) => ({ label: service, value: service }))
      );
    } catch (err) {
      console.error('Error loading framework/service metadata:', err);
      // Non-critical error - fall back to mapping-derived options.
    } finally {
      setFilterMetadataLoading(false);
    }
  };

  // Load current user ID
  const loadCurrentUser = async () => {
    try {
      const userId = await getUserAlias();
      setCurrentUserId(userId);
    } catch (err) {
      console.error('Error loading user ID:', err);
    }
  };

  // Handle vote button click - opens feedback modal
  const handleVoteClick = (item, voteType) => {
    const mappingKey = item.mapping?.mappingKey;
    if (!mappingKey) return;

    setCurrentFeedbackMapping(item);
    setFeedbackType(voteType);
    setFeedbackReason('');
    setShowFeedbackModal(true);
  };

  // Submit feedback (create or update)
  const submitFeedback = async () => {
    if (!currentFeedbackMapping || !feedbackType || !currentUserId) return;

    const mappingKey = currentFeedbackMapping.mapping?.mappingKey;
    if (!mappingKey) return;

    setFeedbackSubmitting(true);
    try {
      const existingFeedback = userFeedback[mappingKey];
      const decision = feedbackType === 'thumbs_up' ? 'agree' : 'disagree';

      let newFeedbackId = existingFeedback?.feedbackId;

      const dataService = getDataService();
      if (existingFeedback?.feedbackId) {
        // Update existing feedback
        await dataService.updateFeedback(mappingKey, existingFeedback.feedbackId, {
          label: feedbackType,
          decision: decision,
          feedback: feedbackReason.trim() || undefined,
        });
      } else {
        // Create new feedback
        const response = await dataService.submitFeedback(mappingKey, {
          feedbackProviderId: currentUserId,
          label: feedbackType,
          decision: decision,
          feedback: feedbackReason.trim() || undefined,
        });
        newFeedbackId = response.feedbackId;
      }

      // Update local state
      setUserFeedback(prev => ({
        ...prev,
        [mappingKey]: {
          label: feedbackType,
          feedback: feedbackReason.trim(),
          feedbackId: newFeedbackId,
        },
      }));

      // Close modal and show success
      setShowFeedbackModal(false);
      setCurrentFeedbackMapping(null);
      setFeedbackReason('');
      setFeedbackType(null);
      setError({
        type: 'success',
        message: `Feedback submitted successfully!`,
      });
      setTimeout(() => setError(null), 3000);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError({
        type: 'error',
        message: `Failed to submit feedback: ${err.message}`,
      });
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  // Cancel feedback modal
  const cancelFeedback = () => {
    setShowFeedbackModal(false);
    setCurrentFeedbackMapping(null);
    setFeedbackReason('');
    setFeedbackType(null);
  };

  const loadMappings = async ({ fullLoad = false } = {}) => {
    const requestId = activeLoadRequestRef.current + 1;
    activeLoadRequestRef.current = requestId;
    setLoading(true);
    try {
      // In direct DynamoDB mode (prod), load full data and keep filtering client-side.
      // In API mode (beta/gamma), keep preview load to avoid fan-out bursts.
      const shouldUsePreviewLoad = !usesDirectDynamoDB && !fullLoad;
      const query = shouldUsePreviewLoad
        ? { limit: PREVIEW_MAPPING_LIMIT, includeReviews: false }
        : { includeReviews: false };

      const dataService = getDataService();
      const rawData = await dataService.getAllMappingsWithReviews(query);
      const data = await filterVisibleMappings(rawData);
      if (requestId !== activeLoadRequestRef.current) {
        return;
      }
      setMappingsData(data);
      setIsPreviewMode(shouldUsePreviewLoad);
      setCanLoadFullMappings(
        !usesDirectDynamoDB && !fullLoad && rawData.length >= PREVIEW_MAPPING_LIMIT
      );
      setControlDetails(new Map());
      setError(null);
      setCurrentPageIndex(1);
    } catch (err) {
      console.error('Error loading mappings:', err);
      setError({
        type: 'error',
        message: `Failed to load mappings: ${err.message}`
      });
    } finally {
      if (requestId === activeLoadRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const loadMappingsForSelectedFrameworks = async ({
    frameworks,
    fullLoad = false,
  }) => {
    if (!frameworks || frameworks.length === 0) {
      return;
    }

    const requestId = activeLoadRequestRef.current + 1;
    activeLoadRequestRef.current = requestId;
    setLoading(true);
    try {
      const dataService = getDataService();
      const perFrameworkLimit = fullLoad ? undefined : PREVIEW_MAPPING_LIMIT;

      const frameworkBatches = await Promise.all(
        frameworks.map((framework) =>
          dataService.getAllMappingsWithReviews({
            frameworkKey: framework.value,
            limit: perFrameworkLimit,
            includeReviews: false,
          })
        )
      );

      const deduped = [];
      const seen = new Set();
      frameworkBatches.flat().forEach((entry) => {
        const key =
          entry.mapping?.mappingKey ||
          `${entry.mapping?.controlKey || ''}#${entry.mapping?.mappedControlKey || ''}`;
        if (!key || seen.has(key)) {
          return;
        }
        seen.add(key);
        deduped.push(entry);
      });
      const visible = await filterVisibleMappings(deduped);

      const hasPotentialMore =
        !fullLoad &&
        frameworkBatches.some((batch) => batch.length >= PREVIEW_MAPPING_LIMIT);

      if (requestId !== activeLoadRequestRef.current) {
        return;
      }
      setMappingsData(visible);
      setIsPreviewMode(!fullLoad);
      setCanLoadFullMappings(hasPotentialMore);
      setControlDetails(new Map());
      setError(null);
      setCurrentPageIndex(1);
    } catch (err) {
      console.error('Error loading framework-scoped mappings:', err);
      setError({
        type: 'error',
        message: `Failed to load framework mappings: ${err.message}`,
      });
    } finally {
      if (requestId === activeLoadRequestRef.current) {
        setLoading(false);
      }
    }
  };

  const scopedControlKeys = useMemo(() => {
    if (selectedCommonControls.length > 0) {
      const keysFromSelection = selectedCommonControls
        .map((control) => control.controlKey)
        .filter(Boolean);
      if (keysFromSelection.length > 0) {
        return Array.from(new Set(keysFromSelection));
      }

      const selectedCodes = new Set(selectedCommonControls.map((control) => control.value));
      return Array.from(
        new Set(
          commonControlsData.controls
            .filter((control) => selectedCodes.has(control.code))
            .map((control) => control.controlKey)
            .filter(Boolean)
        )
      );
    }

    if (selectedObjectives.length > 0) {
      const selectedObjectiveCodes = new Set(selectedObjectives.map((objective) => objective.value));
      return Array.from(
        new Set(
          commonControlsData.controls
            .filter((control) => selectedObjectiveCodes.has(control.objectiveCode))
            .map((control) => control.controlKey)
            .filter(Boolean)
        )
      );
    }

    if (selectedDomains.length > 0) {
      const selectedDomainCodes = new Set(selectedDomains.map((domain) => domain.value));
      return Array.from(
        new Set(
          commonControlsData.controls
            .filter((control) => selectedDomainCodes.has(control.domainCode))
            .map((control) => control.controlKey)
            .filter(Boolean)
        )
      );
    }

    return [];
  }, [
    selectedCommonControls,
    selectedObjectives,
    selectedDomains,
    commonControlsData.controls,
  ]);

  const loadMappingsForControlScope = async ({
    controlKeys,
    fullLoad = false,
  }) => {
    if (!controlKeys || controlKeys.length === 0) {
      return;
    }

    const requestId = activeLoadRequestRef.current + 1;
    activeLoadRequestRef.current = requestId;
    setLoading(true);
    try {
      const dataService = getDataService();
      const uniqueMappings = new Map();
      const dedupedControlKeys = Array.from(new Set(controlKeys.filter(Boolean)));
      const selectedFrameworkSet = new Set(
        selectedFrameworks.map((framework) =>
          normalizeFrameworkKey(framework.value)
        )
      );
      const controlsToQuery = fullLoad
        ? dedupedControlKeys
        : dedupedControlKeys.slice(0, 25);

      // For Common-Controls hierarchy selections, fetch by Common-Control key
      // in adaptive parallel batches. Framework narrowing applied client-side.
      let controlBatchSize = 5;
      const MIN_CONTROL_BATCH_SIZE = 2;
      const MAX_CONTROL_BATCH_SIZE = 6;
      const INTER_BATCH_BACKOFF_MS = 150;
      let exhaustedScopes = true;
      let batchFailures = 0;
      let batchAttempts = 0;

      const addEntry = (entry) => {
        if (selectedFrameworkSet.size > 0) {
          const ck = entry.mapping?.controlKey || '';
          const mck = entry.mapping?.mappedControlKey || '';
          const [lc = '', lv = ''] = ck.split('#');
          const [rc = '', rv = ''] = mck.split('#');
          if (
            !selectedFrameworkSet.has(normalizeFrameworkKey(`${lc}#${lv}`)) &&
            !selectedFrameworkSet.has(normalizeFrameworkKey(`${rc}#${rv}`))
          ) {
            return;
          }
        }
        const key =
          entry.mapping?.mappingKey ||
          `${entry.mapping?.controlKey || ''}#${entry.mapping?.mappedControlKey || ''}`;
        if (key) uniqueMappings.set(key, entry);
      };

      let cursor = 0;
      while (cursor < controlsToQuery.length) {
        // Bail early if a newer load was triggered
        if (requestId !== activeLoadRequestRef.current) return;

        const remaining =
          fullLoad ? undefined : Math.max(PREVIEW_MAPPING_LIMIT - uniqueMappings.size, 0);
        if (remaining !== undefined && remaining <= 0) {
          exhaustedScopes = false;
          break;
        }

        const batch = controlsToQuery.slice(cursor, cursor + controlBatchSize);
        cursor += batch.length;
        batchAttempts += batch.length;
        let batchFailureCount = 0;
        let throttlingFailureCount = 0;

        const batchResults = await Promise.all(
          batch.map((controlKey) =>
            dataService
              .getAllMappingsWithReviews({
                controlKey,
                limit: remaining,
                includeReviews: false,
              })
              .catch((err) => {
                batchFailures += 1;
                batchFailureCount += 1;
                const combined = `${err?.name || ''} ${err?.code || ''} ${err?.message || ''}`;
                if (/429|TooManyRequests|throttl/i.test(combined)) {
                  throttlingFailureCount += 1;
                }
                console.warn(`Failed to fetch mappings for ${controlKey}:`, err);
                return [];
              })
          )
        );

        // Bail early if a newer load was triggered
        if (requestId !== activeLoadRequestRef.current) return;

        batchResults.flat().forEach(addEntry);

        if (throttlingFailureCount > 0) {
          controlBatchSize = Math.max(
            MIN_CONTROL_BATCH_SIZE,
            controlBatchSize - throttlingFailureCount
          );
          await new Promise((resolve) =>
            setTimeout(resolve, INTER_BATCH_BACKOFF_MS)
          );
        } else if (
          batchFailureCount === 0 &&
          controlBatchSize < MAX_CONTROL_BATCH_SIZE
        ) {
          controlBatchSize += 1;
        }
      }

      // Surface error only when every single call failed
      if (batchAttempts > 0 && batchFailures === batchAttempts) {
        throw new Error(
          'All control mapping requests failed. The service may be throttling or unavailable.'
        );
      }

      const deduped = Array.from(uniqueMappings.values());
      const visible = await filterVisibleMappings(deduped);
      const truncatedControlScope =
        !fullLoad && dedupedControlKeys.length > controlsToQuery.length;

      if (requestId !== activeLoadRequestRef.current) {
        return;
      }
      setMappingsData(visible);
      setIsPreviewMode(!fullLoad);
      setCanLoadFullMappings(
        !fullLoad &&
          (deduped.length >= PREVIEW_MAPPING_LIMIT ||
            !exhaustedScopes ||
            truncatedControlScope)
      );
      setControlDetails(new Map());
      setError(null);
      setCurrentPageIndex(1);
    } catch (err) {
      console.error('Error loading control-scoped mappings:', err);
      setError({
        type: 'error',
        message: `Failed to load control mappings: ${err.message}`,
      });
    } finally {
      if (requestId === activeLoadRequestRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (usesDirectDynamoDB) {
      if (!hasLoadedDirectModeRef.current) {
        hasLoadedDirectModeRef.current = true;
        loadMappings({ fullLoad: true });
      }
      return;
    }
    if (!isPreviewMode && mappingsData.length > 0) {
      return;
    }

    if (filterLoadDebounceRef.current) {
      clearTimeout(filterLoadDebounceRef.current);
    }

    filterLoadDebounceRef.current = setTimeout(() => {
      if (scopedControlKeys.length > 0) {
        const frameworkSignature = selectedFrameworks
          .map((framework) => normalizeFrameworkKey(framework.value))
          .sort()
          .join('|');
        const signature = `${[...scopedControlKeys].sort().join('|')}::${frameworkSignature}`;
        if (lastScopedControlSignatureRef.current === signature) {
          return;
        }
        lastScopedControlSignatureRef.current = signature;
        loadMappingsForControlScope({
          controlKeys: scopedControlKeys,
          fullLoad: false,
        });
        return;
      }

      lastScopedControlSignatureRef.current = '';

      if (selectedFrameworks.length > 0) {
        loadMappingsForSelectedFrameworks({
          frameworks: selectedFrameworks,
          fullLoad: false,
        });
        return;
      }
      loadMappings({ fullLoad: false });
    }, FILTER_LOAD_DEBOUNCE_MS);

    return () => {
      if (filterLoadDebounceRef.current) {
        clearTimeout(filterLoadDebounceRef.current);
      }
    };
  }, [
    selectedFrameworks,
    scopedControlKeys,
    usesDirectDynamoDB,
    isPreviewMode,
    mappingsData.length,
  ]);

  // Load user's existing feedback for selected mapping IDs
  const loadUserFeedback = async (mappingIds) => {
    let pendingIds = [];
    try {
      if (!currentUserId || mappingIds.length === 0) return;
      pendingIds = mappingIds.filter(
        (mappingId) => !feedbackLoadingIdsRef.current.has(mappingId)
      );
      if (pendingIds.length === 0) {
        return;
      }
      pendingIds.forEach((mappingId) => feedbackLoadingIdsRef.current.add(mappingId));

      const dataService = getDataService();
      const feedbackMap = await dataService.getUserFeedbackForMappings(
        pendingIds,
        currentUserId
      );

      // Mark every pending ID as checked (null = no feedback found)
      // and merge any returned feedback without clobbering existing entries.
      setUserFeedback((prev) => {
        const next = { ...prev };
        let changed = false;

        pendingIds.forEach((mappingId) => {
          if (!Object.prototype.hasOwnProperty.call(next, mappingId)) {
            next[mappingId] = null;
            changed = true;
          }
        });

        feedbackMap.forEach((value, key) => {
          if (next[key] !== value) {
            next[key] = value;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    } catch (err) {
      console.error('Error loading user feedback:', err);
      // Don't show error to user - feedback loading is non-critical
    } finally {
      pendingIds.forEach((mappingId) => feedbackLoadingIdsRef.current.delete(mappingId));
    }
  };

  // Parse control key into components
  const parseControlKey = (key) => {
    if (!key) {
      return { full: '', catalog: '', version: '', controlId: '' };
    }
    const parts = key.split('#');
    return {
      full: key,
      catalog: parts[0] || '',
      version: parts[1] || '',
      controlId: parts[2] || key,
    };
  };

  const isAwsCatalog = (catalog) => /^AWS[.-]?/i.test(catalog || '');

  const isCommonControlsCatalog = (catalog) =>
    isCommonControlsCatalogName(catalog || '');

  const getMappingSides = (mapping) => {
    const left = parseControlKey(mapping?.controlKey);
    const right = parseControlKey(mapping?.mappedControlKey);

    const leftIsAws = isAwsCatalog(left.catalog);
    const rightIsAws = isAwsCatalog(right.catalog);

    if (leftIsAws && !rightIsAws) {
      return {
        aws: { parsed: left, controlKey: mapping?.controlKey },
        framework: { parsed: right, controlKey: mapping?.mappedControlKey },
        commonControl:
          isCommonControlsCatalog(right.catalog)
            ? { parsed: right, controlKey: mapping?.mappedControlKey }
            : null,
      };
    }

    if (!leftIsAws && rightIsAws) {
      return {
        aws: { parsed: right, controlKey: mapping?.mappedControlKey },
        framework: { parsed: left, controlKey: mapping?.controlKey },
        commonControl:
          isCommonControlsCatalog(left.catalog)
            ? { parsed: left, controlKey: mapping?.controlKey }
            : null,
      };
    }

    const defaultFramework = !leftIsAws
      ? { parsed: left, controlKey: mapping?.controlKey }
      : { parsed: right, controlKey: mapping?.mappedControlKey };

    const commonControl = isCommonControlsCatalog(left.catalog)
      ? { parsed: left, controlKey: mapping?.controlKey }
      : isCommonControlsCatalog(right.catalog)
        ? { parsed: right, controlKey: mapping?.mappedControlKey }
        : null;

    return {
      aws: leftIsAws
        ? { parsed: left, controlKey: mapping?.controlKey }
        : rightIsAws
          ? { parsed: right, controlKey: mapping?.mappedControlKey }
          : { parsed: left, controlKey: mapping?.controlKey },
      framework: defaultFramework,
      commonControl,
    };
  };

  // Get control details by key
  const getControlDetails = (frameworkKey, fullControlKey) => {
    const lookupKey = `${frameworkKey}#${fullControlKey}`;
    return controlDetails.get(lookupKey);
  };

  // Extract unique AWS services from mappings (fallback when metadata load fails)
  const mappingDerivedAwsServices = useMemo(() => {
    const services = new Set();
    mappingsData.forEach(item => {
      const { aws } = getMappingSides(item.mapping);
      if (aws?.parsed?.catalog && isAwsCatalog(aws.parsed.catalog)) {
        // Extract service name from catalog
        // Handles both formats: "AWS-SecurityHub" and "AWS.ControlCatalog"
        const serviceName = aws.parsed.catalog.replace(/^AWS[.-]?/i, '');
        if (serviceName) {
          services.add(serviceName);
        }
      }
    });
    return Array.from(services).sort().map(service => ({
      label: service,
      value: service,
    }));
  }, [mappingsData]);

  const availableAwsServices = useMemo(() => {
    if (awsServiceMetadata.length > 0) {
      return awsServiceMetadata;
    }
    return mappingDerivedAwsServices;
  }, [awsServiceMetadata, mappingDerivedAwsServices]);

  // Extract unique frameworks from mappings (fallback when metadata load fails)
  const mappingDerivedFrameworks = useMemo(() => {
    const frameworks = new Map();
    mappingsData.forEach(item => {
      const { framework } = getMappingSides(item.mapping);
      const parsed = framework?.parsed;
      if (parsed.catalog && parsed.version) {
        if (isCommonControlsCatalog(parsed.catalog)) {
          return;
        }
        const key = `${parsed.catalog}#${parsed.version}`;
        if (!frameworks.has(key)) {
          // Create display label with framework flavor support
          let label = parsed.catalog;
          if (parsed.version) {
            label += ` ${parsed.version}`;
          }
          frameworks.set(key, {
            label: label,
            value: key,
            catalog: parsed.catalog,
            version: parsed.version,
          });
        }
      }
    });
    
    // Sort and group by framework family
    const sorted = Array.from(frameworks.values()).sort((a, b) => 
      a.label.localeCompare(b.label)
    );
    
    return sorted;
  }, [mappingsData]);

  const availableFrameworks = useMemo(() => {
    if (frameworkMetadata.length > 0) {
      return frameworkMetadata;
    }
    return mappingDerivedFrameworks;
  }, [frameworkMetadata, mappingDerivedFrameworks]);

  // Group frameworks by family for better UX (FedRAMP High/Moderate/Low)
  const groupedFrameworkOptions = useMemo(() => {
    const groups = new Map();

    availableFrameworks.forEach(fw => {
      // Extract framework family (e.g., "FedRAMP" from "FedRAMP-High")
      const family = fw.catalog.split('-')[0];
      if (!groups.has(family)) {
        groups.set(family, []);
      }
      groups.get(family).push(fw);
    });

    // Convert to Cloudscape grouped options format
    return Array.from(groups.entries()).map(([family, options]) => ({
      label: family,
      options: options,
    }));
  }, [availableFrameworks]);

  // ==========================================================================
  // Common Controls Cascading Filter Options (NEX-1062)
  // ==========================================================================

  // L1 Domain options (always show all domains)
  const domainOptions = useMemo(() => {
    return commonControlsData.domains.map(domain => ({
      label: `${domain.code} - ${domain.title}`,
      value: domain.code,
      description: domain.description?.substring(0, 100) + (domain.description?.length > 100 ? '...' : ''),
    }));
  }, [commonControlsData.domains]);

  // L2 Objective options (filtered by selected domains - cascading)
  const objectiveOptions = useMemo(() => {
    let objectives = commonControlsData.objectives;

    // If domains are selected, filter objectives to only those under selected domains
    if (selectedDomains.length > 0) {
      const selectedDomainCodes = selectedDomains.map(d => d.value);
      objectives = objectives.filter(obj => selectedDomainCodes.includes(obj.domainCode));
    }

    return objectives.map(objective => ({
      label: `${objective.code} - ${objective.title}`,
      value: objective.code,
      description: objective.description?.substring(0, 100) + (objective.description?.length > 100 ? '...' : ''),
      domainCode: objective.domainCode,
    }));
  }, [commonControlsData.objectives, selectedDomains]);

  // L3 Common Control options (filtered by selected objectives - cascading)
  const commonControlOptions = useMemo(() => {
    let controls = commonControlsData.controls;

    // If objectives are selected, filter controls to only those under selected objectives
    if (selectedObjectives.length > 0) {
      const selectedObjectiveCodes = selectedObjectives.map(o => o.value);
      controls = controls.filter(ctrl => selectedObjectiveCodes.includes(ctrl.objectiveCode));
    } else if (selectedDomains.length > 0) {
      // If only domains are selected (no objectives), filter by domain
      const selectedDomainCodes = selectedDomains.map(d => d.value);
      controls = controls.filter(ctrl => selectedDomainCodes.includes(ctrl.domainCode));
    }

    return controls.map(control => ({
      label: `${control.code} - ${control.title}`,
      value: control.code,
      description: control.description?.substring(0, 100) + (control.description?.length > 100 ? '...' : ''),
      controlKey: control.controlKey,
      objectiveCode: control.objectiveCode,
      domainCode: control.domainCode,
    }));
  }, [commonControlsData.controls, selectedDomains, selectedObjectives]);

  // Clear child selections when parent changes (cascading behavior)
  const handleDomainChange = (selectedOptions) => {
    setSelectedDomains(selectedOptions);
    // Clear L2 and L3 selections if they're no longer valid
    if (selectedOptions.length > 0) {
      const validDomainCodes = selectedOptions.map(d => d.value);
      setSelectedObjectives(prev =>
        prev.filter(obj => validDomainCodes.includes(obj.domainCode))
      );
      setSelectedCommonControls(prev =>
        prev.filter(ctrl => validDomainCodes.includes(ctrl.domainCode))
      );
    }
    setCurrentPageIndex(1);
  };

  const handleObjectiveChange = (selectedOptions) => {
    setSelectedObjectives(selectedOptions);
    // Clear L3 selections if they're no longer valid
    if (selectedOptions.length > 0) {
      const validObjectiveCodes = selectedOptions.map(o => o.value);
      setSelectedCommonControls(prev =>
        prev.filter(ctrl => validObjectiveCodes.includes(ctrl.objectiveCode))
      );
    }
    setCurrentPageIndex(1);
  };

  const handleCommonControlChange = (selectedOptions) => {
    setSelectedCommonControls(selectedOptions);
    setCurrentPageIndex(1);
  };

  // Filter mappings based on selections
  const filteredMappings = useMemo(() => {
    let filtered = mappingsData;

    // Filter by AWS services
    if (selectedAwsServices.length > 0) {
      const serviceValues = selectedAwsServices.map(s => s.value.toLowerCase());
      filtered = filtered.filter(item => {
        const { aws } = getMappingSides(item.mapping);
        const serviceName = aws?.parsed?.catalog
          ? aws.parsed.catalog.replace(/^AWS[.-]?/i, '').toLowerCase()
          : '';
        if (!serviceName) {
          return false;
        }
        return serviceValues.some(sv => serviceName.includes(sv) || sv.includes(serviceName));
      });
    }

    // Filter by frameworks
    if (selectedFrameworks.length > 0) {
      const frameworkValues = new Set(
        selectedFrameworks.map((framework) =>
          normalizeFrameworkKey(framework.value)
        )
      );
      filtered = filtered.filter(item => {
        const left = parseControlKey(item.mapping.controlKey);
        const right = parseControlKey(item.mapping.mappedControlKey);
        const leftFramework = normalizeFrameworkKey(
          `${left.catalog}#${left.version}`
        );
        const rightFramework = normalizeFrameworkKey(
          `${right.catalog}#${right.version}`
        );
        return (
          frameworkValues.has(leftFramework) ||
          frameworkValues.has(rightFramework)
        );
      });
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const awsParsed = parseControlKey(item.mapping.controlKey);
        const fwParsed = parseControlKey(item.mapping.mappedControlKey);
        const awsDetails = getControlDetails(
          `${awsParsed.catalog}#${awsParsed.version}`,
          item.mapping.controlKey
        );
        const fwDetails = getControlDetails(
          `${fwParsed.catalog}#${fwParsed.version}`,
          item.mapping.mappedControlKey
        );

        return (
          item.mapping.controlKey?.toLowerCase().includes(term) ||
          item.mapping.mappedControlKey?.toLowerCase().includes(term) ||
          awsParsed.controlId?.toLowerCase().includes(term) ||
          fwParsed.controlId?.toLowerCase().includes(term) ||
          awsDetails?.title?.toLowerCase().includes(term) ||
          fwDetails?.title?.toLowerCase().includes(term) ||
          awsDetails?.description?.toLowerCase().includes(term) ||
          fwDetails?.description?.toLowerCase().includes(term)
        );
      });
    }

    // ==========================================================================
    // NEX-1062: Filter by Common Controls hierarchy (L1/L2/L3)
    // ==========================================================================
    // Note: This filtering works by matching the mappedControlKey against
    // Common Controls. If direct mappings exist (Common-Controls framework),
    // they will be filtered. Otherwise, this prepares the UI for when
    // transitive mappings are available.

    // Filter by Common Controls (L3) - most specific
    if (selectedCommonControls.length > 0) {
      const selectedControlCodes = selectedCommonControls.map(c => c.value.toLowerCase());
      filtered = filtered.filter(item => {
        const { commonControl } = getMappingSides(item.mapping);
        if (!commonControl?.parsed) {
          return false;
        }
        return selectedControlCodes.includes(commonControl.parsed.controlId.toLowerCase());
      });
    }
    // Filter by Control Objectives (L2) - if no L3 selected
    else if (selectedObjectives.length > 0) {
      const selectedObjectiveCodes = selectedObjectives.map(o => o.value.toLowerCase());
      // Get all L3 controls under selected L2 objectives
      const relatedControlCodes = commonControlsData.controls
        .filter(ctrl => selectedObjectiveCodes.includes(ctrl.objectiveCode.toLowerCase()))
        .map(ctrl => ctrl.code.toLowerCase());

      filtered = filtered.filter(item => {
        const { commonControl } = getMappingSides(item.mapping);
        const controlId = commonControl?.parsed?.controlId?.toLowerCase();
        return controlId ? relatedControlCodes.includes(controlId) : false;
      });
    }
    // Filter by Control Domains (L1) - if no L2 or L3 selected
    else if (selectedDomains.length > 0) {
      const selectedDomainCodes = selectedDomains.map(d => d.value.toLowerCase());
      // Get all L3 controls under selected L1 domains
      const relatedControlCodes = commonControlsData.controls
        .filter(ctrl => selectedDomainCodes.includes(ctrl.domainCode.toLowerCase()))
        .map(ctrl => ctrl.code.toLowerCase());

      filtered = filtered.filter(item => {
        const { commonControl } = getMappingSides(item.mapping);
        const controlId = commonControl?.parsed?.controlId?.toLowerCase();
        return controlId ? relatedControlCodes.includes(controlId) : false;
      });
    }

    return filtered;
  }, [
    mappingsData,
    selectedAwsServices,
    selectedFrameworks,
    searchTerm,
    controlDetails,
    selectedDomains,
    selectedObjectives,
    selectedCommonControls,
    commonControlsData.controls,
  ]);

  // Get mapping status from the mapping's status field
  const getMappingStatus = (mappingWithReviews) => {
    const { mapping } = mappingWithReviews;
    const status = mapping?.status?.toUpperCase() || 'UNKNOWN';

    switch (status) {
      case 'APPROVED':
        return { type: 'success', label: 'Approved', color: 'green' };
      case 'REJECTED':
        return { type: 'error', label: 'Rejected', color: 'red' };
      case 'PENDING':
      case 'IN-REVIEW':
        return { type: 'in-progress', label: 'In Review', color: 'blue' };
      case 'ARCHIVED':
        return { type: 'stopped', label: 'Archived', color: 'grey' };
      default:
        return { type: 'pending', label: status, color: 'grey' };
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedAwsServices([]);
    setSelectedFrameworks([]);
    setSearchTerm('');
    // Clear Common Controls filters (NEX-1062)
    setSelectedDomains([]);
    setSelectedObjectives([]);
    setSelectedCommonControls([]);
    setCurrentPageIndex(1);
  };

  // Calculate pagination
  const startIndex = (currentPageIndex - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = useMemo(
    () => filteredMappings.slice(startIndex, endIndex),
    [filteredMappings, startIndex, endIndex]
  );
  const totalPages = useMemo(
    () => Math.ceil(filteredMappings.length / itemsPerPage),
    [filteredMappings.length, itemsPerPage]
  );

  // Load feedback only for currently visible page items.
  useEffect(() => {
    if (!currentUserId || paginatedItems.length === 0) {
      return;
    }

    const visibleIds = paginatedItems
      .map((item) => item.mapping?.mappingKey)
      .filter(Boolean)
      .filter((id) => !Object.prototype.hasOwnProperty.call(userFeedback, id));

    if (visibleIds.length === 0) {
      return;
    }

    loadUserFeedback(visibleIds);
  }, [currentUserId, paginatedItems, userFeedback]);

  // Lazy-load control metadata for currently visible rows to reduce initial API burst.
  useEffect(() => {
    const loadVisibleControlDetails = async () => {
      if (paginatedItems.length === 0) {
        return;
      }

      const controlsToFetch = [];
      paginatedItems.forEach((item) => {
        const aws = parseControlKey(item.mapping.controlKey);
        const fw = parseControlKey(item.mapping.mappedControlKey);

        if (aws.catalog && aws.version) {
          const lookupKey = `${aws.catalog}#${aws.version}#${item.mapping.controlKey}`;
          if (!controlDetailsRef.current.has(lookupKey)) {
            controlsToFetch.push({
              frameworkKey: `${aws.catalog}#${aws.version}`,
              controlKey: item.mapping.controlKey,
            });
          }
        }

        if (fw.catalog && fw.version) {
          const lookupKey = `${fw.catalog}#${fw.version}#${item.mapping.mappedControlKey}`;
          if (!controlDetailsRef.current.has(lookupKey)) {
            controlsToFetch.push({
              frameworkKey: `${fw.catalog}#${fw.version}`,
              controlKey: item.mapping.mappedControlKey,
            });
          }
        }
      });

      if (controlsToFetch.length === 0) {
        return;
      }

      try {
        const dataService = getDataService();
        const details = await dataService.batchGetFrameworkControls(controlsToFetch);
        setControlDetails((prev) => {
          const merged = new Map(prev);
          details.forEach((value, key) => merged.set(key, value));
          return merged;
        });
      } catch (err) {
        console.warn('Error loading control details for visible mappings:', err);
      }
    };

    loadVisibleControlDetails();
  }, [paginatedItems]);

  if (loading) {
    return (
      <Container>
        <Box textAlign="center" padding="xl">
          <div className="loading-spinner"></div>
          <Box variant="p" margin={{ top: 'm' }}>
            Loading mappings...
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      {/* Header */}
      <Container
        header={
          <Header
            variant="h1"
            description="View and filter AWS service mappings to industry compliance frameworks"
            actions={
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={clearFilters} iconName="remove">
                  Clear Filters
                </Button>
                {isPreviewMode && canLoadFullMappings && (
                  <Button
                    onClick={() => {
                      if (scopedControlKeys.length > 0) {
                        loadMappingsForControlScope({
                          controlKeys: scopedControlKeys,
                          fullLoad: true,
                        });
                        return;
                      }
                      if (selectedFrameworks.length > 0) {
                        loadMappingsForSelectedFrameworks({
                          frameworks: selectedFrameworks,
                          fullLoad: true,
                        });
                        return;
                      }
                      loadMappings({ fullLoad: true });
                    }}
                  >
                    Load Full Results
                  </Button>
                )}
                <Button
                  onClick={() => {
                    if (usesDirectDynamoDB) {
                      loadMappings({ fullLoad: true });
                      return;
                    }

                    if (scopedControlKeys.length > 0) {
                      loadMappingsForControlScope({
                        controlKeys: scopedControlKeys,
                        fullLoad: !isPreviewMode,
                      });
                      return;
                    }
                    if (selectedFrameworks.length > 0) {
                      loadMappingsForSelectedFrameworks({
                        frameworks: selectedFrameworks,
                        fullLoad: !isPreviewMode,
                      });
                      return;
                    }
                    loadMappings({ fullLoad: !isPreviewMode });
                  }}
                  iconName="refresh"
                >
                  Refresh
                </Button>
              </SpaceBetween>
            }
          >
            View Mappings
          </Header>
        }
      >
        {error && (
          <Alert
            type={error.type || 'error'}
            dismissible
            onDismiss={() => setError(null)}
          >
            {error.message}
          </Alert>
        )}

        {/* Filter Panel */}
        <SpaceBetween size="m">
          <ColumnLayout columns={3} variant="text-grid">
            {/* AWS Services Filter */}
            <FormField
              label="AWS Services"
              description="Filter by one or more AWS services"
            >
              <Multiselect
                selectedOptions={selectedAwsServices}
                onChange={({ detail }) => {
                  setSelectedAwsServices(detail.selectedOptions);
                  setCurrentPageIndex(1);
                }}
                options={availableAwsServices}
                placeholder={filterMetadataLoading ? 'Loading services...' : 'Select AWS services...'}
                filteringType="auto"
                tokenLimit={3}
                disabled={filterMetadataLoading && availableAwsServices.length === 0}
                i18nStrings={{
                  tokenLimitShowMore: 'Show more',
                  tokenLimitShowFewer: 'Show fewer',
                }}
              />
            </FormField>

            {/* Framework Filter */}
            <FormField
              label="Industry Frameworks"
              description="Filter by compliance frameworks (supports groupings like FedRAMP High/Moderate/Low)"
            >
              <Multiselect
                selectedOptions={selectedFrameworks}
                onChange={({ detail }) => {
                  setSelectedFrameworks(detail.selectedOptions);
                  setCurrentPageIndex(1);
                }}
                options={groupedFrameworkOptions}
                placeholder={filterMetadataLoading ? 'Loading frameworks...' : 'Select frameworks...'}
                filteringType="auto"
                tokenLimit={3}
                disabled={filterMetadataLoading && groupedFrameworkOptions.length === 0}
                i18nStrings={{
                  tokenLimitShowMore: 'Show more',
                  tokenLimitShowFewer: 'Show fewer',
                }}
              />
            </FormField>

            {/* Text Search */}
            <FormField
              label="Search"
              description="Search by control ID, title, or description"
            >
              <Input
                value={searchTerm}
                onChange={({ detail }) => {
                  setSearchTerm(detail.value);
                  setCurrentPageIndex(1);
                }}
                placeholder="Search mappings..."
                type="search"
                clearAriaLabel="Clear search"
              />
            </FormField>
          </ColumnLayout>

          {/* Common Controls Cascading Filters (NEX-1062) */}
          <Box>
            <Header variant="h3" description="Filter by Compass Ontology hierarchy (cascading filters)">
              Common Controls
            </Header>
          </Box>
          <ColumnLayout columns={3} variant="text-grid">
            {/* L1 - Control Domain Filter */}
            <FormField
              label="Control Domain (L1)"
              description="Top-level control category"
            >
              <Multiselect
                selectedOptions={selectedDomains}
                onChange={({ detail }) => handleDomainChange(detail.selectedOptions)}
                options={domainOptions}
                placeholder={commonControlsLoading ? "Loading..." : "Select domain(s)..."}
                filteringType="auto"
                tokenLimit={2}
                disabled={commonControlsLoading}
                statusType={commonControlsLoading ? "loading" : "finished"}
                i18nStrings={{
                  tokenLimitShowMore: 'Show more',
                  tokenLimitShowFewer: 'Show fewer',
                }}
              />
            </FormField>

            {/* L2 - Control Objective Filter */}
            <FormField
              label="Control Objective (L2)"
              description={selectedDomains.length > 0 ? `Filtered by ${selectedDomains.length} domain(s)` : "Select a domain first or choose any objective"}
            >
              <Multiselect
                selectedOptions={selectedObjectives}
                onChange={({ detail }) => handleObjectiveChange(detail.selectedOptions)}
                options={objectiveOptions}
                placeholder={commonControlsLoading ? "Loading..." : "Select objective(s)..."}
                filteringType="auto"
                tokenLimit={2}
                disabled={commonControlsLoading}
                statusType={commonControlsLoading ? "loading" : "finished"}
                i18nStrings={{
                  tokenLimitShowMore: 'Show more',
                  tokenLimitShowFewer: 'Show fewer',
                }}
              />
            </FormField>

            {/* L3 - Common Control Filter */}
            <FormField
              label="Common Control (L3)"
              description={selectedObjectives.length > 0 ? `Filtered by ${selectedObjectives.length} objective(s)` : (selectedDomains.length > 0 ? `Filtered by ${selectedDomains.length} domain(s)` : "Select domain/objective first or choose any control")}
            >
              <Multiselect
                selectedOptions={selectedCommonControls}
                onChange={({ detail }) => handleCommonControlChange(detail.selectedOptions)}
                options={commonControlOptions}
                placeholder={commonControlsLoading ? "Loading..." : "Select control(s)..."}
                filteringType="auto"
                tokenLimit={2}
                disabled={commonControlsLoading}
                statusType={commonControlsLoading ? "loading" : "finished"}
                i18nStrings={{
                  tokenLimitShowMore: 'Show more',
                  tokenLimitShowFewer: 'Show fewer',
                }}
              />
            </FormField>
          </ColumnLayout>

          {/* Filter Summary */}
          <Box>
            <SpaceBetween direction="horizontal" size="xs">
              {isPreviewMode && (
                <Badge color="blue">
                  Preview mode: showing up to {PREVIEW_MAPPING_LIMIT} mappings
                </Badge>
              )}
              <Badge color="blue">{filteredMappings.length} mappings found</Badge>
              {selectedAwsServices.length > 0 && (
                <Badge color="green">{selectedAwsServices.length} service(s) selected</Badge>
              )}
              {selectedFrameworks.length > 0 && (
                <Badge color="green">{selectedFrameworks.length} framework(s) selected</Badge>
              )}
              {/* Common Controls filter badges (NEX-1062) */}
              {selectedDomains.length > 0 && (
                <Badge color="grey">{selectedDomains.length} domain(s)</Badge>
              )}
              {selectedObjectives.length > 0 && (
                <Badge color="grey">{selectedObjectives.length} objective(s)</Badge>
              )}
              {selectedCommonControls.length > 0 && (
                <Badge color="grey">{selectedCommonControls.length} control(s)</Badge>
              )}
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </Container>

      {/* Mappings Table */}
      <Container>
        <Table
          items={paginatedItems}
          pagination={
            <Pagination
              currentPageIndex={currentPageIndex}
              onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
              pagesCount={totalPages}
            />
          }
          columnDefinitions={[
            {
              id: 'awsControl',
              header: 'AWS Control',
              width: 300,
              cell: item => {
                const { aws } = getMappingSides(item.mapping);
                const parsed = aws?.parsed || parseControlKey(item.mapping.controlKey);
                const details = getControlDetails(
                  `${parsed.catalog}#${parsed.version}`,
                  aws?.controlKey || item.mapping.controlKey
                );
                return (
                  <SpaceBetween size="xs">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Badge color="blue">{parsed.catalog}</Badge>
                      <Box variant="strong">{parsed.controlId}</Box>
                    </SpaceBetween>
                    {details?.title && (
                      <Box variant="small">{details.title}</Box>
                    )}
                    {details?.description && (
                      <Box variant="small" color="text-body-secondary">
                        {details.description}
                      </Box>
                    )}
                  </SpaceBetween>
                );
              },
            },
            {
              id: 'frameworkControl',
              header: 'Framework Control',
              width: 300,
              cell: item => {
                const { framework } = getMappingSides(item.mapping);
                const parsed = framework?.parsed || parseControlKey(item.mapping.mappedControlKey);
                const details = getControlDetails(
                  `${parsed.catalog}#${parsed.version}`,
                  framework?.controlKey || item.mapping.mappedControlKey
                );
                return (
                  <SpaceBetween size="xs">
                    <SpaceBetween direction="horizontal" size="xs">
                      <Badge>{parsed.catalog} {parsed.version}</Badge>
                      <Box variant="strong">{parsed.controlId}</Box>
                    </SpaceBetween>
                    {details?.title && (
                      <Box variant="small">{details.title}</Box>
                    )}
                    {details?.description && (
                      <Box variant="small" color="text-body-secondary">
                        {details.description}
                      </Box>
                    )}
                  </SpaceBetween>
                );
              },
            },
            {
              id: 'status',
              header: 'Status',
              width: 150,
              cell: item => {
                const status = getMappingStatus(item);
                return (
                  <Badge color={status.color}>{status.label}</Badge>
                );
              },
            },
            {
              id: 'lastUpdated',
              header: 'Last Updated',
              width: 150,
              cell: item => {
                const timestamp = item.mapping?.timestamp;
                if (!timestamp) return <Box>-</Box>;
                try {
                  const date = new Date(timestamp);
                  return (
                    <Box>
                      {date.toLocaleDateString()}
                    </Box>
                  );
                } catch {
                  return <Box>-</Box>;
                }
              },
            },
            {
              id: 'feedback',
              header: 'Your Feedback',
              width: 180,
              cell: item => {
                const mappingKey = item.mapping?.mappingKey;
                const existingFeedback = userFeedback[mappingKey];

                return (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button
                      variant={existingFeedback?.label === 'thumbs_up' ? 'primary' : 'normal'}
                      iconName="thumbs-up"
                      onClick={() => handleVoteClick(item, 'thumbs_up')}
                      ariaLabel="Thumbs up"
                    />
                    <Button
                      variant={existingFeedback?.label === 'thumbs_down' ? 'primary' : 'normal'}
                      iconName="thumbs-down"
                      onClick={() => handleVoteClick(item, 'thumbs_down')}
                      ariaLabel="Thumbs down"
                    />
                    {existingFeedback && (
                      <Icon
                        name="status-positive"
                        variant="success"
                        size="small"
                      />
                    )}
                  </SpaceBetween>
                );
              },
            },
          ]}
          variant="container"
          stickyHeader
          wrapLines
          empty={
            <Box textAlign="center" padding="l">
              <SpaceBetween size="m">
                <Box variant="p" color="text-body-secondary">
                  No mappings found matching your criteria
                </Box>
                {(selectedAwsServices.length > 0 || selectedFrameworks.length > 0 || searchTerm) && (
                  <Button onClick={clearFilters}>
                    Clear filters
                  </Button>
                )}
              </SpaceBetween>
            </Box>
          }
          header={
            <Header
              counter={`(${filteredMappings.length} total)`}
            >
              Control Mappings
            </Header>
          }
        />
      </Container>

      {/* Feedback Modal (NEX-1064) */}
      <Modal
        visible={showFeedbackModal}
        onDismiss={cancelFeedback}
        header={
          feedbackType === 'thumbs_up'
            ? 'Submit Positive Feedback'
            : 'Submit Negative Feedback'
        }
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={cancelFeedback}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitFeedback}
                loading={feedbackSubmitting}
              >
                Submit Feedback
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          {currentFeedbackMapping && (
            <Box>
              <Box variant="awsui-key-label">Mapping</Box>
              <Box>
                {parseControlKey(currentFeedbackMapping.mapping?.controlKey).controlId}
                {' → '}
                {parseControlKey(currentFeedbackMapping.mapping?.mappedControlKey).controlId}
              </Box>
            </Box>
          )}

          <FormField
            label="Feedback (optional)"
            description={
              feedbackType === 'thumbs_up'
                ? 'Let us know why this mapping is helpful'
                : 'Help us understand what could be improved'
            }
          >
            <Textarea
              value={feedbackReason}
              onChange={({ detail }) => setFeedbackReason(detail.value)}
              placeholder={
                feedbackType === 'thumbs_up'
                  ? 'This mapping accurately reflects the control relationship...'
                  : 'This mapping could be improved because...'
              }
              rows={4}
            />
          </FormField>

          {feedbackType === 'thumbs_down' && (
            <Alert type="info">
              Your feedback helps us improve mapping quality. Our team will review
              flagged mappings for potential updates.
            </Alert>
          )}
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
};

export default ViewMappings;
